/**
 * Translation Service - Multiple free translation APIs
 * Supports: LibreTranslate, MyMemory, Google Translate (free tier)
 */

/**
 * Translate text using LibreTranslate (Free, Open Source)
 * @param {string} text - Text to translate
 * @param {string} sourceLang - Source language code (e.g., 'es', 'auto')
 * @param {string} targetLang - Target language code (e.g., 'en')
 * @returns {Promise<string>} Translated text
 */
export const translateWithLibreTranslate = async (text, sourceLang = 'auto', targetLang = 'en') => {
  try {
    const response = await fetch('https://libretranslate.com/translate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        q: text,
        source: sourceLang,
        target: targetLang,
        format: 'text'
      })
    });

    if (!response.ok) {
      throw new Error(`LibreTranslate API error: ${response.status}`);
    }

    const data = await response.json();
    return data.translatedText;
  } catch (error) {
    console.error('LibreTranslate error:', error);
    throw error;
  }
};

/**
 * Translate text using MyMemory API (Free, no API key required)
 * @param {string} text - Text to translate
 * @param {string} sourceLang - Source language code (e.g., 'es')
 * @param {string} targetLang - Target language code (e.g., 'en')
 * @returns {Promise<string>} Translated text
 */
export const translateWithMyMemory = async (text, sourceLang = 'es', targetLang = 'en') => {
  try {
    const langPair = `${sourceLang}|${targetLang}`;
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${langPair}`;
    
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`MyMemory API error: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.responseStatus !== 200) {
      throw new Error(`MyMemory translation failed: ${data.responseStatus}`);
    }

    return data.responseData.translatedText;
  } catch (error) {
    console.error('MyMemory error:', error);
    throw error;
  }
};

/**
 * Translate text using Google Translate (Free, unofficial API)
 * Note: This uses the unofficial Google Translate API and may have rate limits
 * @param {string} text - Text to translate
 * @param {string} sourceLang - Source language code (e.g., 'es', 'auto')
 * @param {string} targetLang - Target language code (e.g., 'en')
 * @returns {Promise<string>} Translated text
 */
export const translateWithGoogle = async (text, sourceLang = 'auto', targetLang = 'en') => {
  try {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`;
    
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Google Translate API error: ${response.status}`);
    }

    const data = await response.json();
    
    // Google Translate returns an array structure
    if (data && data[0] && data[0][0] && data[0][0][0]) {
      return data[0][0][0];
    }
    
    throw new Error('Invalid response from Google Translate');
  } catch (error) {
    console.error('Google Translate error:', error);
    throw error;
  }
};

/**
 * Main translation function with automatic fallback
 * Tries multiple services in order: LibreTranslate -> MyMemory -> Google
 * @param {string} text - Text to translate
 * @param {string} sourceLang - Source language code (default: 'auto' or 'es')
 * @param {string} targetLang - Target language code (default: 'en')
 * @returns {Promise<{success: boolean, translatedText: string, service: string, error?: string}>}
 */
export const translateText = async (text, sourceLang = 'auto', targetLang = 'en') => {
  if (!text || !text.trim()) {
    return {
      success: false,
      translatedText: '',
      service: 'none',
      error: 'Empty text provided'
    };
  }

  // Try LibreTranslate first (best quality, open source)
  try {
    const translatedText = await translateWithLibreTranslate(text, sourceLang, targetLang);
    return {
      success: true,
      translatedText,
      service: 'LibreTranslate'
    };
  } catch (error) {
    console.warn('LibreTranslate failed, trying MyMemory...');
  }

  // Fallback to MyMemory
  try {
    const sourceLangCode = sourceLang === 'auto' ? 'es' : sourceLang;
    const translatedText = await translateWithMyMemory(text, sourceLangCode, targetLang);
    return {
      success: true,
      translatedText,
      service: 'MyMemory'
    };
  } catch (error) {
    console.warn('MyMemory failed, trying Google Translate...');
  }

  // Fallback to Google Translate
  try {
    const translatedText = await translateWithGoogle(text, sourceLang, targetLang);
    return {
      success: true,
      translatedText,
      service: 'Google Translate'
    };
  } catch (error) {
    console.error('All translation services failed');
    return {
      success: false,
      translatedText: text, // Return original text
      service: 'none',
      error: 'All translation services failed'
    };
  }
};

/**
 * Detect language of text (uses LibreTranslate)
 * @param {string} text - Text to detect language
 * @returns {Promise<string>} Language code (e.g., 'es', 'en')
 */
export const detectLanguage = async (text) => {
  try {
    const response = await fetch('https://libretranslate.com/detect', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        q: text
      })
    });

    if (!response.ok) {
      throw new Error('Language detection failed');
    }

    const data = await response.json();
    
    // Returns array of detected languages with confidence scores
    if (data && data.length > 0) {
      return data[0].language;
    }
    
    return 'unknown';
  } catch (error) {
    console.error('Language detection error:', error);
    return 'unknown';
  }
};

/**
 * Translate Spanish to English (convenience function)
 * @param {string} text - Spanish text to translate
 * @returns {Promise<{success: boolean, translatedText: string, service: string}>}
 */
export const spanishToEnglish = async (text) => {
  return translateText(text, 'es', 'en');
};

/**
 * Translate English to Spanish (convenience function)
 * @param {string} text - English text to translate
 * @returns {Promise<{success: boolean, translatedText: string, service: string}>}
 */
export const englishToSpanish = async (text) => {
  return translateText(text, 'en', 'es');
};

/**
 * Check and correct grammar using LanguageTool API (Free)
 * @param {string} text - Text to check
 * @param {string} language - Language code (e.g., 'en-US', 'es')
 * @returns {Promise<{success: boolean, correctedText: string, matches: Array}>}
 */
export const checkGrammar = async (text, language = 'en-US') => {
  try {
    const response = await fetch('https://api.languagetool.org/v2/check', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        text: text,
        language: language,
        enabledOnly: 'false'
      })
    });

    if (!response.ok) {
      throw new Error(`LanguageTool API error: ${response.status}`);
    }

    const data = await response.json();
    
    // Apply corrections automatically
    let correctedText = text;
    const matches = data.matches || [];
    
    // Sort matches by offset in reverse order to maintain positions
    matches.sort((a, b) => b.offset - a.offset);
    
    // Apply each correction
    for (const match of matches) {
      if (match.replacements && match.replacements.length > 0) {
        const replacement = match.replacements[0].value;
        const start = match.offset;
        const end = start + match.length;
        correctedText = correctedText.substring(0, start) + replacement + correctedText.substring(end);
      }
    }
    
    return {
      success: true,
      correctedText,
      matches,
      originalText: text
    };
  } catch (error) {
    console.error('Grammar check error:', error);
    return {
      success: false,
      correctedText: text,
      matches: [],
      error: error.message
    };
  }
};

/**
 * Translate and correct grammar (replaces OpenAI functionality)
 * 1. Detects language
 * 2. If Spanish, translates to English
 * 3. Corrects grammar in English
 * @param {string} text - Text to process
 * @returns {Promise<{success: boolean, processedText: string, steps: Array}>}
 */
export const translateAndCorrect = async (text) => {
  const steps = [];
  let processedText = text;
  
  try {
    // Step 1: Detect language
    const detectedLang = await detectLanguage(text);
    steps.push({ step: 'detect', language: detectedLang });
    
    // Step 2: Translate if not English
    if (detectedLang !== 'en' && detectedLang !== 'unknown') {
      const translationResult = await translateText(text, detectedLang, 'en');
      if (translationResult.success) {
        processedText = translationResult.translatedText;
        steps.push({ 
          step: 'translate', 
          from: detectedLang, 
          to: 'en',
          service: translationResult.service 
        });
      }
    }
    
    // Step 3: Check and correct grammar
    const grammarResult = await checkGrammar(processedText, 'en-US');
    if (grammarResult.success && grammarResult.matches.length > 0) {
      processedText = grammarResult.correctedText;
      steps.push({ 
        step: 'grammar', 
        corrections: grammarResult.matches.length 
      });
    }
    
    return {
      success: true,
      processedText,
      originalText: text,
      steps
    };
  } catch (error) {
    console.error('Translate and correct error:', error);
    return {
      success: false,
      processedText: text,
      originalText: text,
      steps,
      error: error.message
    };
  }
};

export default {
  translateText,
  translateWithLibreTranslate,
  translateWithMyMemory,
  translateWithGoogle,
  detectLanguage,
  spanishToEnglish,
  englishToSpanish,
  checkGrammar,
  translateAndCorrect
};
